import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  ChevronLeft,
  Mail,
  Shield,
  UserCircle,
  Users,
} from 'lucide-react';
import governance from '../../content/governanceBodies.json';
import BrandTextHighlight from '../../components/public/BrandTextHighlight.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import SectionAmbientGradient from '../../components/public/SectionAmbientGradient.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { clsx } from '../../utils/cn.js';
import { ENTES_ADMIN_ORGANOS } from '../../constants/entesAdministrativos.js';

const ORGAN_ICONS = {
  consejo_administracion: Users,
  junta_vigilancia: Shield,
};

function memberInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function MemberRow({ member, delayMs }) {
  const photo = member.imagen?.trim();
  return (
    <ScrollReveal delayMs={delayMs} from="up">
      <li className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5">
        {photo ? (
          <img
            src={assetUrl(photo)}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-[#0d2b7f]/15"
          />
        ) : (
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0d2b7f]/10 text-xs font-bold text-[#0d2b7f] ring-1 ring-[#0d2b7f]/15"
            aria-hidden
          >
            {memberInitials(member.nombre)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-slate-800">{member.nombre}</p>
          {member.cargo ? <p className="text-xs text-slate-500">{member.cargo}</p> : null}
        </div>
      </li>
    </ScrollReveal>
  );
}

function OrganCard({ organ, members, index }) {
  const Icon = ORGAN_ICONS[organ.id] ?? Users;

  return (
    <ScrollReveal delayMs={Math.min(index, 4) * 60} from="up">
      <PremiumCard hover variant="light" className="flex h-full flex-col">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1acb12]/12 ring-1 ring-[#1acb12]/25">
            <Icon className="h-6 w-6 text-[#00A63E]" strokeWidth={1.75} aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-slate-800">{organ.label}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{organ.description}</p>
          </div>
        </div>
        {members.length > 0 ? (
          <ul className="mt-6 space-y-2">
            {members.map((member, i) => (
              <MemberRow key={member.id} member={member} delayMs={80 + i * 40} />
            ))}
          </ul>
        ) : (
          <p className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500">
            Integrantes en actualización.
          </p>
        )}
      </PremiumCard>
    </ScrollReveal>
  );
}

export default function GovernanceBodiesPage() {
  const { hero, management, organsSection } = governance;
  const gerentePending = management.name.toLowerCase().includes('por confirmar');

  const [grouped, setGrouped] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/v1/public/entes-administrativos');
        if (!cancelled && data.success) {
          setGrouped(data.data);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'No se pudieron cargar los integrantes.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const organsWithMembers = useMemo(
    () =>
      ENTES_ADMIN_ORGANOS.map((organ) => ({
        ...organ,
        members: grouped?.[organ.id] ?? [],
      })),
    [grouped]
  );

  const loadingMembers = grouped === null && !loadError;

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-white/10">
        <SectionAmbientGradient />
        <div className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Volver al inicio
          </Link>
          <ScrollReveal className="mt-10 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">{hero.eyebrow}</p>
            <h1 className="mt-4 font-display text-4xl font-bold text-white md:text-5xl">{hero.title}</h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              <BrandTextHighlight text={hero.subtitle} className="font-semibold text-accent-lime" />
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#00A63E]">Dirección ejecutiva</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-accent-blue md:text-3xl">
              {management.sectionTitle}
            </h2>
          </ScrollReveal>

          <ScrollReveal delayMs={80} className="mx-auto mt-10 max-w-3xl">
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(13,43,127,0.08)] ring-1 ring-[#0d2b7f]/10 lg:p-10">
              <div
                className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#1acb12]/10 blur-2xl"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-[#0d2b7f]/8 blur-3xl"
                aria-hidden
              />

              <div className="relative flex flex-col items-center gap-6 text-center sm:flex-row sm:items-start sm:text-left">
                <span
                  className={clsx(
                    'flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl text-2xl font-bold shadow-inner',
                    gerentePending
                      ? 'bg-slate-100 text-slate-400 ring-1 ring-dashed ring-slate-300'
                      : 'bg-gradient-to-br from-[#0d2b7f] to-[#1acb12] text-white ring-2 ring-white'
                  )}
                >
                  {gerentePending ? (
                    <UserCircle className="h-14 w-14" strokeWidth={1.25} aria-hidden />
                  ) : (
                    memberInitials(management.name)
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#00A63E]">{management.role}</p>
                  <h3
                    className={clsx(
                      'mt-2 font-display text-2xl font-bold md:text-3xl',
                      gerentePending ? 'italic text-slate-500' : 'text-slate-900'
                    )}
                  >
                    {management.name}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{management.bio}</p>
                  {management.email ? (
                    <a
                      href={`mailto:${management.email}`}
                      className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#1acb12]/30 bg-[#1acb12]/8 px-4 py-2 text-sm font-semibold text-[#0d2b7f] transition hover:bg-[#1acb12]/15"
                    >
                      <Mail className="h-4 w-4 text-[#00A63E]" aria-hidden />
                      {management.email}
                    </a>
                  ) : null}
                </div>

                <span
                  className="hidden shrink-0 rounded-xl bg-[#0d2b7f]/8 p-4 ring-1 ring-[#0d2b7f]/15 sm:flex"
                  aria-hidden
                >
                  <Briefcase className="h-10 w-10 text-[#0d2b7f]" strokeWidth={1.5} />
                </span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <ScrollReveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">
              {organsSection.eyebrow}
            </p>
            <h2 className="mt-3 font-display text-2xl font-bold text-white md:text-3xl">{organsSection.title}</h2>
            <p className="mt-4 text-slate-400">{organsSection.description}</p>
          </ScrollReveal>

          {loadingMembers ? (
            <div className="mt-12 flex justify-center py-16">
              <BrandLoading label="Cargando integrantes…" />
            </div>
          ) : (
            <>
              {loadError ? (
                <p className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                  {loadError}
                </p>
              ) : null}
              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {organsWithMembers.map((organ, index) => (
                  <OrganCard key={organ.id} organ={organ} members={organ.members} index={index} />
                ))}
              </div>
            </>
          )}

          <ScrollReveal delayMs={120} className="mt-14 text-center">
            <p className="text-sm text-slate-500">
              ¿Desea comunicarse con la gerencia?{' '}
              <Link to="/contacto" className="font-semibold text-accent-lime hover:text-white">
                Escríbanos desde contacto
              </Link>
              {' · '}
              <Link to="/normatividad" className="font-semibold text-accent-lime hover:text-white">
                Consulte la normatividad
              </Link>
            </p>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
